import type { OrgAccommodationRequest } from '~/gql/accommodations/accommodations'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'

type IProps = {
  request: OrgAccommodationRequest
  formatDate: (value: unknown) => string
  busy: boolean
  onCancel: () => void
  onSave: (input: { response: string, handled: boolean }) => void
}

/** Traiter une demande d'aménagement (#327) : la lire, dire ce qui est mis en place, la clore. */
function AccommodationDialog({ request, formatDate, busy, onCancel, onSave }: IProps) {
  const { t } = useTranslation()
  const [response, setResponse] = useState(request.response ?? '')
  const [handled, setHandled] = useState(request.status === 'HANDLED')

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    onSave({ response, handled })
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{request.learnerName}</DialogTitle>
            <DialogDescription>{t('accommodations.sentOn', { date: formatDate(request.updatedAt) })}</DialogDescription>
          </DialogHeader>
          <p className="whitespace-pre-line rounded-md bg-muted p-3 text-sm">{request.needs}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="accommodationResponse">{t('accommodations.response')}</Label>
            <textarea id="accommodationResponse" rows={4} maxLength={2000} value={response} onChange={e => setResponse(e.target.value)} aria-describedby="accommodationResponseHelp" className="rounded-md border border-input bg-transparent px-3 py-2 text-sm" />
            <p id="accommodationResponseHelp" className="text-xs text-muted-foreground">{t('accommodations.responseHelp')}</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={handled} onChange={e => setHandled(e.target.checked)} />
            {t('accommodations.markHandled')}
          </label>
          {request.handledAt && <p className="text-xs text-muted-foreground">{t('accommodations.handledOn', { date: formatDate(request.handledAt) })}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={busy}>{t('common.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AccommodationDialog
